import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

/**
 * Reusable FAQ accordion for service/city/blog pages.
 * FAQ JSON-LD is injected once at the page level.
 */
export default function FaqAccordion({ faqs, testid = "faq-accordion" }) {
  const [open, setOpen] = useState(0);

  if (!faqs?.length) return null;

  return (
    <div data-testid={testid} className="space-y-2">
      {faqs.map((f, i) => {
        const isOpen = open === i;
        return (
          <div
            key={i}
            className="border-b border-white/10"
            data-testid={`${testid}-item-${i}`}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? -1 : i)}
              aria-expanded={isOpen}
              className="w-full py-5 md:py-6 flex items-center justify-between gap-6 text-left group"
              data-testid={`${testid}-toggle-${i}`}
            >
              <h3
                className="font-serif-display text-[var(--iw-ivory)] text-xl md:text-2xl leading-tight group-hover:text-[var(--iw-gold)] transition-colors"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {f.q}
              </h3>
              <span
                className="shrink-0 w-9 h-9 grid place-items-center border transition-all"
                style={{
                  borderColor: isOpen
                    ? "var(--iw-gold)"
                    : "rgba(255,255,255,0.15)",
                  background: isOpen
                    ? "rgba(212,175,55,0.12)"
                    : "transparent",
                  transform: isOpen ? "rotate(45deg)" : "rotate(0)",
                }}
              >
                <Plus
                  size={14}
                  className={
                    isOpen ? "text-[var(--iw-gold)]" : "text-[var(--iw-faint)]"
                  }
                />
              </span>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <p
                    className="text-[var(--iw-muted)] leading-relaxed pb-6 md:pb-7 pr-12 text-[15px] md:text-base"
                    data-testid={`${testid}-answer-${i}`}
                  >
                    {f.a}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
